import Checkbox from '@allesauto/core/checkbox';
import Slider from '@allesauto/core/slider';
import TextLink from '@allesauto/core/textLink';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { getSlug } from '@allesauto/utils/funcs';
import debounce from 'lodash/debounce';
import React, { FC, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { FuelFilterStateProps } from '@/store/redux/actions/pages/pcp';

export interface FuelFilterProps extends SchemaTypes.FuelTypeFacet {
  value: FuelFilterStateProps;
  onChange: (value: FuelFilterStateProps) => void;
}

const FuelFilter: FC<React.PropsWithChildren<FuelFilterProps>> = ({
  fuelTypes = [],
  maxFuelConsumption: maxFuelConsumptionDefault,
  maxPowerConsumption: maxPowerConsumptionDefault,
  value,
  onChange,
}) => {
  const [showAllFuelTypes, setShowAllFuelTypes] = useState(false);
  const intl = useIntl();

  const trackingIdentifierFuel = 'aa-filter--fuel';
  const trackingIdentifierFuelFossil = `${trackingIdentifierFuel}-fossil`;
  const trackingIdentifierFuelPower = `${trackingIdentifierFuel}-power`;

  const handleCheckboxChange = useCallback(
    (item: string, checked: boolean) => {
      const fuelType = [...(value.fuelType ?? [])];

      if (checked) {
        fuelType.push(item);
      } else {
        fuelType.splice(fuelType.indexOf(item), 1);
      }

      onChange({ fuelType });
    },
    [value, onChange]
  );

  const maxFuelConsumptionChange = useCallback(
    debounce((v) => onChange({ maxFuelConsumption: v as number }), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const maxPowerConsumptionChange = useCallback(
    debounce((v) => onChange({ maxPowerConsumption: v as number }), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const fuelTypesToShow = showAllFuelTypes
    ? fuelTypes
    : (fuelTypes || []).slice(0, 5);

  const tooltipFormatter = (value: number | number[] | null) => {
    const tranlationTooltip = translate(
      intl,
      'ui.PCP.FILTERS.FUEL_TYPE.FUEL_CONSUMPTION_SLIDER_STEP_LABEL'
    );

    return value !== null && !Array.isArray(value)
      ? `${tranlationTooltip} ${value}`
      : null;
  };

  return (
    <div>
      <p className="font-weight-normal pb-30px text-p1 text-white">
        {translate(intl, 'ui.PCP.FILTERS.FUEL_TYPE.FUEL_TITLE')}
      </p>
      {fuelTypesToShow &&
        fuelTypesToShow.map((ft, i) => {
          return ft ? (
            <Checkbox
              id={`${trackingIdentifierFuel}-type-${getSlug(ft.name)}`}
              key={i}
              label={ft.displayName}
              checked={value.fuelType && value.fuelType.includes(ft.name)}
              onChange={(v) => handleCheckboxChange(ft.name, v)}
              className="!flex pb-20px"
              darkBg
            />
          ) : null;
        })}
      {fuelTypes && fuelTypes.length > 5 && (
        <TextLink
          text={
            showAllFuelTypes
              ? translate(intl, 'ui.PCP.FILTERS.FUEL_TYPE.VIEW_LESS_LINK')
              : translate(intl, 'ui.PCP.FILTERS.FUEL_TYPE.VIEW_ALL_LINK')
          }
          icon={null}
          isDarkBg={true}
          onClick={() => {
            setShowAllFuelTypes(!showAllFuelTypes);
          }}
        />
      )}
      <p className="font-weight-normal pb-10px pt-40px text-p1 text-white">
        {translate(intl, 'ui.PCP.FILTERS.FUEL_TYPE.FUEL_CONSUMPTION_LABEL')}
      </p>
      <div
        className="-mt-20px w-[98%]"
        data-tracking-id={trackingIdentifierFuelPower}
      >
        <Slider
          id={`${trackingIdentifierFuelPower}-consumption`}
          min={0}
          max={maxFuelConsumptionDefault}
          value={[value.maxFuelConsumption ?? maxFuelConsumptionDefault]}
          onChange={maxFuelConsumptionChange}
          rangeLabelText={translate(
            intl,
            'ui.PCP.FILTERS.FUEL_TYPE.FUEL_CONSUMPTION_SLIDER_STEP_LABEL'
          )}
          step={1}
          darkBg={true}
          range={false}
          tooltipFormatter={tooltipFormatter}
        />
      </div>
      <p className="font-weight-normal pb-10px pt-[50px] text-p1 text-white">
        {translate(intl, 'ui.PCP.FILTERS.FUEL_TYPE.POWER_CONSUMPTION_LABEL')}
      </p>
      <div
        className="-mt-20px w-[98%]"
        data-tracking-id={trackingIdentifierFuelFossil}
      >
        <Slider
          id={`${trackingIdentifierFuelFossil}-consumption`}
          min={0}
          max={maxPowerConsumptionDefault}
          value={[value.maxPowerConsumption ?? maxPowerConsumptionDefault]}
          onChange={maxPowerConsumptionChange}
          rangeLabelText={translate(
            intl,
            'ui.PCP.FILTERS.FUEL_TYPE.POWER_CONSUMPTION_SLIDER_STEP_LABEL'
          )}
          step={1}
          darkBg={true}
          range={false}
          tooltipFormatter={tooltipFormatter}
        />
      </div>
    </div>
  );
};

export default FuelFilter;
